import { useCallback, useEffect, useState } from 'react';
import { getDepositoNotaFiscalAnexos } from '@spark/services/deposito';
import { notification } from '@spark/ui-components/utils/notification';
import { toApiDate } from '@spark/utils/formatters';

const normalizeFilters = (filtros) => (
  Object.fromEntries(
    Object.entries(filtros)
      .filter(([, value]) => value !== undefined && value !== null && value !== '')
      .map(([key, value]) => [key, value instanceof Date ? toApiDate(value) : value])
  )
);

export const useDepositoNotaFiscalAnexos = (notaId) => {
  const [anexos, setAnexos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtros, setFiltros] = useState({});
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [quantidadePorPagina, setQuantidadePorPagina] = useState(10);
  const [totalRegistros, setTotalRegistros] = useState(0);

  const carregarAnexos = useCallback(async () => {
    if (!notaId) {
      setAnexos([]);
      setTotalRegistros(0);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const params = {
        PaginaAtual: paginaAtual,
        QuantidadePorPagina: quantidadePorPagina,
        IdDepositoNotaFiscal: notaId,
        ...normalizeFilters(filtros)
      };

      const response = await getDepositoNotaFiscalAnexos(params);
      const data = response?.data || response || {};
      const registros = Array.isArray(data.registros) ? data.registros : [];

      setAnexos(registros);
      setTotalRegistros(data.totalRegistros || registros.length);
    } catch (error) {
      console.error('Erro ao carregar anexos da nota fiscal:', error);
      notification.error(
        error?.message || 'Nao foi possivel carregar anexos',
        'Erro ao carregar anexos'
      );
      setAnexos([]);
      setTotalRegistros(0);
    } finally {
      setLoading(false);
    }
  }, [notaId, filtros, paginaAtual, quantidadePorPagina]);

  useEffect(() => {
    carregarAnexos();
  }, [carregarAnexos]);

  useEffect(() => {
    setPaginaAtual(1);
  }, [notaId]);

  const handleFiltrosChange = useCallback((novosFiltros) => {
    setFiltros(novosFiltros);
    setPaginaAtual(1);
  }, []);

  const handlePaginationChange = useCallback((paginationState) => {
    if (paginationState.pageSize !== quantidadePorPagina) {
      setQuantidadePorPagina(paginationState.pageSize);
      setPaginaAtual(1);
    } else if (paginationState.pageIndex !== undefined) {
      setPaginaAtual(paginationState.pageIndex + 1);
    }
  }, [quantidadePorPagina]);

  const resetFiltros = useCallback(() => {
    setFiltros({});
    setPaginaAtual(1);
    setQuantidadePorPagina(10);
  }, []);

  const reload = useCallback(() => {
    carregarAnexos();
  }, [carregarAnexos]);

  return {
    anexos,
    loading,
    filtros,
    paginaAtual,
    quantidadePorPagina,
    totalRegistros,
    handleFiltrosChange,
    handlePaginationChange,
    resetFiltros,
    reload
  };
};
